/**
 * @file `usePassedPartIds` — TanStack-backed read of the session
 *       learner's passed-Part set for one course (umbrella #1446 Part B).
 * @module @labcraft/lab/ui/hooks/use-passed-part-ids
 *
 * Backs the course-landing Parts path: a Part whose assessment the
 * learner has passed reads **Done** regardless of the `currentChapter`
 * pointer (the completion-loop fix). Auth-scoped
 * (`GET /api/progress/:courseId`), kept off the public course summary so
 * per-learner state never rides the shared course response.
 *
 * On a fetch failure the query surfaces `isError` (after TanStack's
 * default retries) rather than masking the failure as "nothing passed":
 * the landing shows a retry affordance instead of silently downgrading a
 * passed Part back to incomplete, which would re-open the very loop this
 * umbrella closes.
 *
 * Convention (see `use_progress.ts`): hooks live in this directory and
 * never call `useQuery` outside it; components import the workspace hook.
 */

import { useQuery } from '@tanstack/react-query'
import { useClient } from '@labcraft/core/frontend/react'
import type { Lab } from '../../client/index.ts'
import { qkPassedParts } from './query_keys.ts'

/**
 * Subscribe to the learner's passed-Part ids for `courseId`. The first
 * reader drives the fetch; subsequent readers share the cache under
 * {@link qkPassedParts}.
 *
 * Disabled when `courseId` is empty — the home hero calls this with the
 * pointer's course id, which is `''` for a learner with no chapter
 * pointer; firing then would `GET /api/progress/` and 404. The landing
 * always passes a real id, so it is unaffected.
 *
 * @param courseId - the course whose passed-Part set to read
 * @returns the TanStack query result (`data` is `string[]` on success;
 *   `isError` on a persistent fetch failure)
 */
export function usePassedPartIds(courseId: string) {
  const client = useClient<Lab>()
  return useQuery<string[]>({
    queryKey: qkPassedParts(courseId),
    queryFn: () => client.courses.passedPartIds(courseId),
    enabled: courseId.length > 0,
  })
}
